import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { getUserStats, getRatingTier } from '@callbridge/ui';
import { useFeatureFlag } from '../hooks';
import { trackEvent } from '../lib/analytics';
import {
  SpadeSvg,
  HeartSvg,
  DiamondSvg,
  ClubSvg,
  LogoutSvg,
  ChevronRightSvg,
  TrophySvg,
  ChartSvg,
  GearSvg,
} from '../components/SuitIcons';

const TIER_COLORS: Record<string, string> = {
  Bronze: '#cd7f32',
  Silver: '#c0c0c0',
  Gold: '#ffd700',
  Platinum: '#00cfff',
  Diamond: '#b388ff',
  Master: '#ff4081',
};

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const stats = user ? getUserStats(user) : null;
  const tier = user ? getRatingTier(user.rating) : null;
  const tierColor = TIER_COLORS[tier?.label ?? ''] ?? '#f0c040';

  // A/B test: does promoting Multiplayer above Single Player increase
  // multiplayer engagement? See docs/FEATURE_FLAGS.md.
  const modeOrderVariant = useFeatureFlag('home_mode_order');
  const multiplayerFirst = modeOrderVariant === 'multiplayer_first';

  useEffect(() => {
    trackEvent('experiment_exposure', { flag: 'home_mode_order', variant: modeOrderVariant });
  }, [modeOrderVariant]);

  const singlePlayerCard = (
    <Link to="/play/single" className="mode-card mode-single">
      <div className="mode-card-glow" />
      <div className="mode-icon-wrap">
        <SpadeSvg className="mode-suit-icon" />
      </div>
      <div className="mode-text">
        <h2>Single Player</h2>
        <p>Challenge 3 AI opponents</p>
      </div>
      <ChevronRightSvg />
    </Link>
  );

  const multiplayerCard = (
    <Link to="/play/multiplayer" className="mode-card mode-multi">
      <div className="mode-card-glow" />
      <div className="mode-icon-wrap">
        <ClubSvg className="mode-suit-icon" />
      </div>
      <div className="mode-text">
        <h2>Multiplayer</h2>
        <p>Play with friends online</p>
      </div>
      <ChevronRightSvg />
    </Link>
  );

  return (
    <div className="page home-page">
      {/* Floating SVG background suits */}
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      {/* Top bar */}
      <div className="page-topbar">
        <div className="page-brand">
          <SpadeSvg className="brand-icon" />
          <span className="brand-text">Call Bridge</span>
        </div>
        <button className="page-logout" onClick={logout}>
          <LogoutSvg />
          Logout
        </button>
      </div>

      {/* Player card */}
      {user && (
        <div className="player-card">
          <div className="player-card-glow" />
          <div className="player-top">
            <div
              className="player-avatar"
              style={{ '--tier-color': tierColor } as React.CSSProperties}
            >
              <span className="avatar-letter">{user.username[0]?.toUpperCase()}</span>
            </div>
            <div className="player-info">
              <h2 className="player-name">{user.username}</h2>
              <div className="player-tier" style={{ color: tierColor }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill={tierColor}>
                  <polygon points="12,2 15,9 22,9 16.5,14 18.5,21 12,17 5.5,21 7.5,14 2,9 9,9" />
                </svg>
                {tier?.label ?? 'Unranked'} — {user.rating}
              </div>
            </div>
          </div>
          {stats && (
            <div className="player-stats-row">
              <div className="player-stat">
                <span className="player-stat-val">{stats.gamesPlayed}</span>
                <span className="player-stat-lbl">Played</span>
              </div>
              <div className="player-stat">
                <span className="player-stat-val">{stats.gamesWon}</span>
                <span className="player-stat-lbl">Wins</span>
              </div>
              <div className="player-stat">
                <span className="player-stat-val">{stats.winRate}%</span>
                <span className="player-stat-lbl">Win Rate</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Primary game modes */}
      <div className="home-modes">
        {multiplayerFirst ? (
          <>
            {multiplayerCard}
            {singlePlayerCard}
          </>
        ) : (
          <>
            {singlePlayerCard}
            {multiplayerCard}
          </>
        )}
      </div>

      {/* Secondary nav */}
      <div className="home-secondary">
        <Link to="/leaderboard" className="sec-card sec-gold">
          <TrophySvg className="sec-svg-icon" />
          <div>
            <h3>Leaderboard</h3>
            <p>Top players</p>
          </div>
        </Link>
        <Link to="/profile" className="sec-card sec-blue">
          <ChartSvg className="sec-svg-icon" />
          <div>
            <h3>Profile</h3>
            <p>Stats & history</p>
          </div>
        </Link>
        <Link to="/settings" className="sec-card sec-muted">
          <GearSvg className="sec-svg-icon" />
          <div>
            <h3>Settings</h3>
            <p>Preferences</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
